import React from "react";
import * as ReactDOMServer from "react-dom/server";
import L from "leaflet";
import { useMap } from "react-leaflet";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { InfoPopup } from "../helpers/PolylinesComponents";
import variableColors from "../../_App.module.scss";
import { dynamicTextColor, getAcronym } from "../../utils/helpers";
import TripProgressBar from "../helpers/TripProgressBar";
import { configAgencyName, statuteMile } from "../../config";
import { BoldMaroonNodata } from "../helpers/Helpers";

const { darkColor } = variableColors;

export const LegendDiv = styled(InfoPopup)`
  background: inherit;
  p {
    line-height: 1.2;
  }
  div.div-progress {
    width: 100%;
  }
`;

const SelectedBusViewMapLegend = ({
  currentTripDataFromRedux,
  currentBusData,
  speedUnit,
}: {
  currentTripDataFromRedux: CurrentTripData;
  currentBusData: BusInfo;
  speedUnit: string;
}): JSX.Element => {
  const { t } = useTranslation();
  const leafletMap: L.Map = useMap();

  const {
    directionId,
    routeShortName,
    tripId,
    routeInfo: { color: routeColor, id: routeId, longName: routeLongName },
  } = currentTripDataFromRedux;

  //* Adding legend
  React.useEffect(() => {
    if (!leafletMap) return;

    if (leafletMap) {
      const spanStyle = { textAlign: "center" as const, minWidth: "32px", padding: "1px 3px" };
      const legend = new L.Control({ position: "bottomright" }) as L.Control;

      legend.onAdd = (): HTMLElement => {
        const div: HTMLDivElement = L.DomUtil.create("div", "map-route-legend");
        const legendContent = (
          <LegendDiv $width={"auto"}>
            <p>
              {`${t("Linia")}:`}{" "}
              <span
                style={{
                  ...spanStyle,
                  color: dynamicTextColor(routeColor),
                  backgroundColor: routeColor,
                  border: `1px solid ${darkColor}`,
                }}
              >
                {/* //* Show Acronym if agency is Miami Beach */}
                {configAgencyName === ("MiamiBeach" as string)
                  ? getAcronym(routeShortName)
                  : routeShortName || routeId || routeLongName}
              </span>
            </p>
            <p>
              {`${t("Kierunek")}:`} <span style={spanStyle}>{directionId || t("b/d")}</span>
            </p>
            <p>
              {`${t("Kurs")}:`} <span style={spanStyle}>{tripId || t("b/d")}</span>
            </p>
            <p>
              {`${t("Postęp")}:`}{" "}
              {speedUnit === "kmh" ? (
                <span style={spanStyle}>
                  {`${(currentBusData?.distanceAlongTrip / 1000).toFixed(1)}/${(currentBusData?.tripLength / 1000).toFixed(
                    1
                  )} km` || t("b/d")}
                </span>
              ) : speedUnit === "mph" ? (
                <span style={spanStyle}>
                  {`${(currentBusData?.distanceAlongTrip / (1000 * statuteMile)).toFixed(1)}/${(
                    currentBusData?.tripLength /
                    (1000 * statuteMile)
                  ).toFixed(1)} miles` || t("b/d")}
                </span>
              ) : (
                <BoldMaroonNodata />
              )}
            </p>

            <div className="div-progress">
              <TripProgressBar bus={currentBusData} realRouteColor={true} width={10} showInfo={true} strokeLinecap="round" />
            </div>

            <p className="small text-muted">
              {`${t("Dokąd")}:`} <span style={spanStyle}>{currentBusData?.headsign || t("b/d")}</span>
            </p>
            <p className="small text-muted">
              {`${t("Shape")}:`} <span style={spanStyle}>{currentBusData?.tripPattern || t("b/d")}</span>
            </p>
          </LegendDiv>
        ) as JSX.Element;
        div.innerHTML = ReactDOMServer.renderToString(legendContent);
        return div;
      };

      legend.addTo(leafletMap);

      return () => {
        if (legend) {
          legend.remove();
        }
      };
    }
  }, [currentBusData, directionId, leafletMap, routeColor, routeId, routeLongName, routeShortName, speedUnit, t, tripId]);

  return <React.Fragment>{null}</React.Fragment>;
};

export default SelectedBusViewMapLegend;
